import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';
import { useCompanyAccountingStore } from '../../stores/companyAccountingStore';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useDriverStore } from '../../stores/driverStore';

const { width: screenWidth } = Dimensions.get('window');
const isTablet = screenWidth > 768;

interface AnalyticsOverviewScreenProps {
  navigation?: any;
}

export const AnalyticsOverviewScreen: React.FC<AnalyticsOverviewScreenProps> = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Store data
  const { selectedSummary, fetchSummaryByPeriod } = useCompanyAccountingStore();
  const { vehicles, fetchVehicles } = useVehicleStore();
  const { drivers, fetchDrivers } = useDriverStore();

  // KPI State
  const [kpis, setKpis] = useState({
    totalRevenue: 0,
    totalProfit: 0,
    fleetUtilization: 0,
    activeDrivers: 0,
    avgDailyRent: 0,
    avgIncomePerVehicle: 0,
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedMonth, selectedYear]);

  const loadAnalyticsData = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Load all data
      await Promise.all([
        fetchSummaryByPeriod(selectedYear, selectedMonth),
        fetchVehicles(),
        fetchDrivers(),
      ]);

      calculateKPIs();

    } catch (error) {
      console.error('❌ Error loading analytics data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculateKPIs = () => {
    // Total Revenue (rent from drivers + other income)
    const totalRevenue = (selectedSummary?.total_driver_rent_income || 0) + (selectedSummary?.total_other_income || 0);

    // Total Profit
    const totalProfit = selectedSummary?.net_profit || 0;

    // Fleet Utilization (vehicles with active assignments)
    const activeVehicles = vehicles.filter(v => v.carStatus === 'work').length;
    const fleetUtilization = vehicles.length > 0 ? (activeVehicles / vehicles.length) * 100 : 0;

    // Active Drivers
    const activeDrivers = drivers.filter(d => d.status === 'active').length;

    // Average Daily Rent (use total days from summary if available)
    const totalDays = selectedSummary?.active_driver_count 
      ? selectedSummary.active_driver_count * 30 // Estimate 30 days per driver
      : 0;
    const avgDailyRent = selectedSummary?.total_driver_rent_income && totalDays > 0
      ? selectedSummary.total_driver_rent_income / totalDays
      : 0;

    // Average Rent Per Vehicle (per month)
    const avgIncomePerVehicle = selectedSummary?.total_driver_rent_income && selectedSummary.vehicle_count > 0
      ? selectedSummary.total_driver_rent_income / selectedSummary.vehicle_count
      : 0;

    setKpis({
      totalRevenue,
      totalProfit,
      fleetUtilization,
      activeDrivers,
      avgDailyRent,
      avgIncomePerVehicle,
    });
  };

  const onRefresh = () => {
    loadAnalyticsData(true);
  };

  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerLeft}>
        <Ionicons name="bar-chart" size={24} color={theme.colors.primary} />
        <Text style={styles.headerTitle}>Analytics Overview</Text>
      </View>
      <View style={styles.periodSelector}>
        <Text style={styles.periodText}>
          {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
          })}
        </Text>
      </View>
    </View>
  );

  const renderKPICards = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Key Performance Indicators</Text>
      <View style={styles.kpiGrid}>
        {/* Total Revenue */}
        <View style={[styles.kpiCard, { borderLeftColor: '#3B82F6' }]}>
          <View style={styles.kpiIconContainer}>
            <Ionicons name="cash-outline" size={28} color="#3B82F6" />
          </View>
          <View style={styles.kpiContent}>
            <Text style={styles.kpiLabel}>Total Revenue</Text>
            <Text style={[styles.kpiValue, { color: '#3B82F6' }]}>
              {formatCurrency(kpis.totalRevenue)}
            </Text>
            <Text style={styles.kpiSubtext}>Rent + Income</Text>
          </View>
        </View>

        {/* Total Profit */}
        <View style={[styles.kpiCard, { borderLeftColor: kpis.totalProfit >= 0 ? '#10B981' : '#EF4444' }]}>
          <View style={styles.kpiIconContainer}>
            <Ionicons name="trending-up-outline" size={28} color={kpis.totalProfit >= 0 ? '#10B981' : '#EF4444'} />
          </View>
          <View style={styles.kpiContent}>
            <Text style={styles.kpiLabel}>Total Profit</Text>
            <Text style={[styles.kpiValue, { color: kpis.totalProfit >= 0 ? '#10B981' : '#EF4444' }]}>
              {formatCurrency(kpis.totalProfit)}
            </Text>
            <Text style={styles.kpiSubtext}>After expenses</Text>
          </View>
        </View>

        {/* Fleet Utilization */}
        <View style={[styles.kpiCard, { borderLeftColor: '#8B5CF6' }]}>
          <View style={styles.kpiIconContainer}>
            <Ionicons name="car-sport-outline" size={28} color="#8B5CF6" />
          </View>
          <View style={styles.kpiContent}>
            <Text style={styles.kpiLabel}>Fleet Utilization</Text>
            <Text style={[styles.kpiValue, { color: '#8B5CF6' }]}>
              {formatPercentage(kpis.fleetUtilization)}
            </Text>
            <Text style={styles.kpiSubtext}>{vehicles.filter(v => v.carStatus === 'work').length}/{vehicles.length} vehicles</Text>
          </View>
        </View>

        {/* Active Drivers */}
        <View style={[styles.kpiCard, { borderLeftColor: '#F59E0B' }]}>
          <View style={styles.kpiIconContainer}>
            <Ionicons name="people-outline" size={28} color="#F59E0B" />
          </View>
          <View style={styles.kpiContent}>
            <Text style={styles.kpiLabel}>Active Drivers</Text>
            <Text style={[styles.kpiValue, { color: '#F59E0B' }]}>
              {kpis.activeDrivers}
            </Text>
            <Text style={styles.kpiSubtext}>Currently working</Text>
          </View>
        </View>

        {/* Average Daily Rent */}
        <View style={[styles.kpiCard, { borderLeftColor: '#EC4899' }]}>
          <View style={styles.kpiIconContainer}>
            <Ionicons name="calendar-outline" size={28} color="#EC4899" />
          </View>
          <View style={styles.kpiContent}>
            <Text style={styles.kpiLabel}>Avg Daily Share</Text>
            <Text style={[styles.kpiValue, { color: '#EC4899' }]}>
              {formatCurrency(kpis.avgDailyRent)}
            </Text>
            <Text style={styles.kpiSubtext}>Per vehicle/day</Text>
          </View>
        </View>

        {/* Average Income Per Vehicle */}
        <View style={[styles.kpiCard, { borderLeftColor: '#14B8A6' }]}>
          <View style={styles.kpiIconContainer}>
            <Ionicons name="stats-chart-outline" size={28} color="#14B8A6" />
          </View>
          <View style={styles.kpiContent}>
            <Text style={styles.kpiLabel}>Avg Income/Vehicle</Text>
            <Text style={[styles.kpiValue, { color: '#14B8A6' }]}>
              {formatCurrency(kpis.avgIncomePerVehicle)}
            </Text>
            <Text style={styles.kpiSubtext}>Monthly average</Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderQuickAccess = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Analytics Reports</Text>
      <Text style={styles.sectionDescription}>Access detailed financial analysis and reports</Text>

      {/* Company Profit Dashboard */}
      <TouchableOpacity
        style={styles.reportCard}
        onPress={() => navigation?.navigate('CompanyProfit')}
        activeOpacity={0.7}
      >
        <View style={[styles.reportIconContainer, { backgroundColor: '#10B981' + '20' }]}>
          <Ionicons name="trending-up" size={32} color="#10B981" />
        </View>
        <View style={styles.reportContent}>
          <Text style={styles.reportTitle}>Company Profit Dashboard</Text>
          <Text style={styles.reportSubtitle}>
            Comprehensive financial analysis with profit trends, vehicle profitability, and detailed breakdowns
          </Text>
          <View style={styles.reportFeatures}>
            <View style={styles.reportFeature}>
              <Ionicons name="checkmark-circle" size={16} color="#10B981" />
              <Text style={styles.reportFeatureText}>Profit trend charts</Text>
            </View>
            <View style={styles.reportFeature}>
              <Ionicons name="checkmark-circle" size={16} color="#10B981" />
              <Text style={styles.reportFeatureText}>Vehicle profitability analysis</Text>
            </View>
            <View style={styles.reportFeature}>
              <Ionicons name="checkmark-circle" size={16} color="#10B981" />
              <Text style={styles.reportFeatureText}>Detailed financial breakdowns</Text>
            </View>
          </View>
        </View>
        <Ionicons name="chevron-forward" size={24} color={theme.colors.gray400} />
      </TouchableOpacity>

      {/* Placeholder for future reports */}
      <View style={styles.comingSoonCard}>
        <Ionicons name="bar-chart-outline" size={32} color={theme.colors.gray400} />
        <View style={styles.comingSoonContent}>
          <Text style={styles.comingSoonTitle}>More Reports Coming Soon</Text>
          <Text style={styles.comingSoonSubtitle}>
            Driver performance, vehicle utilization, revenue forecasting, and more
          </Text>
        </View>
      </View>
    </View>
  );

  const renderInsights = () => {
    const profitMargin = kpis.totalRevenue > 0 ? (kpis.totalProfit / kpis.totalRevenue) * 100 : 0;
    const utilizationStatus = kpis.fleetUtilization >= 80 ? 'excellent' : kpis.fleetUtilization >= 60 ? 'good' : 'needs attention';

    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Quick Insights</Text>
        <View style={styles.insightsList}>
          {/* Profit Margin Insight */}
          <View style={styles.insightCard}>
            <View style={[styles.insightIcon, { backgroundColor: profitMargin >= 20 ? '#10B981' + '20' : '#F59E0B' + '20' }]}>
              <Ionicons
                name={profitMargin >= 20 ? 'trending-up' : 'alert-circle'}
                size={20}
                color={profitMargin >= 20 ? '#10B981' : '#F59E0B'}
              />
            </View>
            <View style={styles.insightContent}>
              <Text style={styles.insightTitle}>Profit Margin: {formatPercentage(profitMargin)}</Text>
              <Text style={styles.insightText}>
                {profitMargin >= 20
                  ? 'Excellent profit margin! Keep up the good work.'
                  : 'Consider optimizing expenses to improve profit margin.'}
              </Text>
            </View>
          </View>

          {/* Fleet Utilization Insight */}
          <View style={styles.insightCard}>
            <View style={[styles.insightIcon, { backgroundColor: utilizationStatus === 'excellent' ? '#10B981' + '20' : '#3B82F6' + '20' }]}>
              <Ionicons
                name="car-sport"
                size={20}
                color={utilizationStatus === 'excellent' ? '#10B981' : '#3B82F6'}
              />
            </View>
            <View style={styles.insightContent}>
              <Text style={styles.insightTitle}>Fleet Utilization: {utilizationStatus}</Text>
              <Text style={styles.insightText}>
                {utilizationStatus === 'excellent'
                  ? `${formatPercentage(kpis.fleetUtilization)} of vehicles are assigned. Great utilization!`
                  : `${formatPercentage(kpis.fleetUtilization)} of vehicles assigned. ${vehicles.filter(v => v.carStatus !== 'work').length} vehicles available.`}
              </Text>
            </View>
          </View>

          {/* Active Drivers Insight */}
          <View style={styles.insightCard}>
            <View style={[styles.insightIcon, { backgroundColor: '#F59E0B' + '20' }]}>
              <Ionicons name="people" size={20} color="#F59E0B" />
            </View>
            <View style={styles.insightContent}>
              <Text style={styles.insightTitle}>{kpis.activeDrivers} Active Drivers</Text>
              <Text style={styles.insightText}>
                {kpis.activeDrivers === 0
                  ? 'No active drivers currently.'
                  : `${kpis.activeDrivers} driver${kpis.activeDrivers !== 1 ? 's are' : ' is'} actively working this month.`}
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading analytics...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderHeader()}

      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {renderKPICards()}
        {renderQuickAccess()}
        {renderInsights()}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  headerTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  periodSelector: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.md,
  },
  periodText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.gray50,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.gray600,
  },
  section: {
    padding: theme.spacing.md,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  sectionDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginBottom: theme.spacing.md,
  },

  // KPI Cards
  kpiGrid: {
    gap: theme.spacing.md,
  },
  kpiCard: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  kpiIconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: theme.colors.gray100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiContent: {
    flex: 1,
  },
  kpiLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: 'bold' as any,
    marginBottom: 2,
  },
  kpiSubtext: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.gray500,
  },

  // Report Cards
  reportCard: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.md,
    alignItems: 'center',
    gap: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  reportIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reportContent: {
    flex: 1,
  },
  reportTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  reportSubtitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    lineHeight: 18,
    marginBottom: theme.spacing.sm,
  },
  reportFeatures: {
    gap: 4,
  },
  reportFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  reportFeatureText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.gray600,
  },

  // Coming Soon Card
  comingSoonCard: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    alignItems: 'center',
    gap: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    borderStyle: 'dashed',
  },
  comingSoonContent: {
    flex: 1,
  },
  comingSoonTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.gray600,
    marginBottom: 4,
  },
  comingSoonSubtitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray500,
    lineHeight: 18,
  },

  // Insights
  insightsList: {
    gap: theme.spacing.md,
  },
  insightCard: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    gap: theme.spacing.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  insightIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  insightContent: {
    flex: 1,
  },
  insightTitle: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  insightText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    lineHeight: 18,
  },
});

export default AnalyticsOverviewScreen;
